import { z } from "zod";
import { requestQuestionFieldsSchema } from "./request-questions.js";
// The customer workspace contract of Milestone C (CHT-736..741; docs/architecture/milestone-c-contracts.md sections 4.7 to 4.10;
// ADR 0013). FROZEN at the Wave 0 commit. Lane D1 builds Trips, Saved and navigation; lane D2 builds the Action Center and
// Profile.
//
// Same rules as `customer-memory.ts`: strict top-level shapes, ISO 8601 text for times, codes repeated from the domain and
// parity-tested in `@lafayette/agent-contract`. A customer view selects explicit columns: no internal note, staff id, routing
// detail, reason, confidence or provider reference ever appears. A key that is not listed here fails the parse (fail closed).
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
const uuid = z.string().regex(UUID);
const instant = z.iso.datetime({ offset: false });
const localDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u);
/** An IANA zone name such as `Europe/Paris`. The server validates it against the runtime zone list. */
const timeZone = z.string().min(1).max(64);
const cursor = z.string().min(1).max(512);
const version = z.number().int().min(1).max(2_147_483_647);
const pageLimit = z.coerce.number().int().min(1).max(50).default(20);
// --- codes (parity-tested) ----------------------------------------------------------------------------------------------
export const tripStatusValues = [
    "PLANNING",
    "UPCOMING",
    "ACTIVE",
    "COMPLETED",
    "CANCELLED",
];
export const savedItemTargetTypeValues = ["CATALOG_ITEM", "ITINERARY", "TRIP"];
export const actionBucketValues = ["NEEDS_YOU", "WITH_CONCIERGE", "CONFIRMED", "SAVED"];
/** Phase 2.6 kinds plus `PAYMENT_REQUEST` (CHT-738): a payment that is not linked to a concierge request. */
export const actionSourceKindValues = [
    "CONCIERGE_REQUEST",
    "HANDOFF",
    "SAVED_ITEM",
    "PAYMENT_REQUEST",
];
/** What the customer can do next (CHT-738 plan item 5). The action calls the owning domain service, never the projection. */
export const actionNextStepValues = [
    "COMPLETE_DRAFT",
    "ANSWER_QUESTION",
    "OPEN_PAYMENT",
    "OPEN_CONVERSATION",
    "VIEW_CONFIRMATION",
    "VIEW_SAVED",
    "NONE",
];
// --- deep links (CHT-740) -----------------------------------------------------------------------------------------------
/**
 * Every in-app, chat-card, Stream push and Expo push link resolves through this one target type. The resolver checks
 * ownership and availability on the server before it navigates; an unauthorized, deleted or stale target lands on the tab
 * root with a neutral notice and never shows the id.
 */
export const deepLinkTargetSchema = z.discriminatedUnion("kind", [
    z.strictObject({ kind: z.literal("CONVERSATION") }),
    z.strictObject({ kind: z.literal("TRIP"), tripId: uuid }),
    z.strictObject({ kind: z.literal("ITINERARY"), tripId: uuid, itineraryId: uuid }),
    z.strictObject({ kind: z.literal("ACTION"), actionId: z.string().min(1).max(96) }),
    z.strictObject({ kind: z.literal("SAVED_ITEM"), savedItemId: uuid }),
    z.strictObject({ kind: z.literal("CATALOG_ITEM"), catalogItemId: uuid }),
    z.strictObject({ kind: z.literal("PROFILE") }),
    z.strictObject({ kind: z.literal("MEMORY") }),
]);
/** The Expo Router paths of the targets (scheme `lafayette://`). Lane D1 owns the route files; the paths are frozen. */
export const DEEP_LINK_PATHS = Object.freeze({
    CONVERSATION: "/concierge",
    TRIP: "/trips/[tripId]",
    ITINERARY: "/trips/[tripId]/itineraries/[itineraryId]",
    ACTION: "/actions/[actionId]",
    SAVED_ITEM: "/saved/[savedItemId]",
    CATALOG_ITEM: "/catalog/[catalogItemId]",
    PROFILE: "/profile",
    MEMORY: "/memory",
});
// --- Action Center v2 (derived, ADR 0013) -------------------------------------------------------------------------------
/** `concierge_request:<uuid>`, `handoff:<uuid>`, `saved_item:<uuid>` or `payment_request:<uuid>`. Stable across transitions. */
export const ACTION_ID_PATTERN = /^(?:concierge_request|handoff|saved_item|payment_request):[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
export const customerActionItemV2Schema = z.strictObject({
    id: z.string().regex(ACTION_ID_PATTERN),
    sourceKind: z.enum(actionSourceKindValues),
    sourceId: uuid,
    bucket: z.enum(actionBucketValues),
    /** A neutral display code (`REQUEST_*`, `CONNECTING_TO_CONCIERGE`, `CONCIERGE_IN_CONVERSATION`, `PAYMENT_*`, `SAVED`). */
    statusCode: z.string().min(1).max(64),
    title: z.string().min(1).max(200).nullable(),
    nextStep: z.enum(actionNextStepValues),
    /** The owning entity and its context. A client opens the entity by these ids and reads it live. */
    links: z.strictObject({
        target: deepLinkTargetSchema,
        tripId: uuid.nullable(),
        conversation: z.boolean(),
    }),
    /**
     * The payment an item waits for: from the payment row only (minor units and a currency, never a client amount). Set for a
     * payment request item and for a request in `PAYMENT_REQUIRED`; `null` otherwise. Lane D2 (CHT-738) added the key; it is
     * optional on the type so a producer that has no payment (a trip's open actions) may leave it out.
     */
    payment: z
        .strictObject({
        paymentRequestId: uuid,
        status: z.string().min(1).max(32),
        amountMinor: z.number().int().min(0),
        currency: z.string().length(3),
        expiresAt: instant,
    })
        .nullable()
        .optional(),
    /**
     * CHT-751. The question a request in `AWAITING_CUSTOMER` waits on, in the DETAIL of an action only (a list item leaves it `null`): its id, the
     * prompt and the typed fields of the form. The customer answers it with `POST /customer/concierge-requests/:requestId/questions/:questionId/answer`.
     * `null` for every other item and for a request that has waited since before CHT-751 (it has no question row: it is answered through the
     * free-text respond). Optional on the type for a producer that has none.
     */
    question: z
        .strictObject({
        questionId: uuid,
        prompt: z.string().min(1).max(1000),
        fields: requestQuestionFieldsSchema,
    })
        .nullable()
        .optional(),
    /** When the customer last saw it change (the owning entity's customer-visible time), never an internal edit. */
    updatedAt: instant,
});
/** An ended request or payment of the HISTORY view: the same shape with no bucket. It has no detail (the detail is 404). */
export const customerActionHistoryItemSchema = customerActionItemV2Schema.extend({
    bucket: z.null(),
});
export const customerActionsQuerySchema = z.strictObject({
    /** `ACTIVE` lists the four buckets; `HISTORY` lists ended requests and payments of the last 180 days. */
    view: z.enum(["ACTIVE", "HISTORY"]).default("ACTIVE"),
    bucket: z.enum(actionBucketValues).optional(),
    cursor: cursor.optional(),
    limit: pageLimit,
});
export const customerActionsPageSchema = z.strictObject({
    /** ACTIVE: items with a bucket. HISTORY: items with `bucket: null`. */
    items: z.array(z.union([customerActionItemV2Schema, customerActionHistoryItemSchema])).max(50),
    /** Counts of the ACTIVE view per bucket, computed in the same statement (one snapshot) as the page. */
    counts: z.strictObject({
        NEEDS_YOU: z.number().int().min(0),
        WITH_CONCIERGE: z.number().int().min(0),
        CONFIRMED: z.number().int().min(0),
        SAVED: z.number().int().min(0),
    }),
    nextCursor: cursor.nullable(),
});
/** `POST /customer/concierge-requests/:requestId/respond` (MC-D2-7): the customer's answer to the question of a request. */
export const customerRequestRespondBodySchema = z.strictObject({
    response: z.string().trim().min(1).max(2000),
});
// --- Trips (CHT-736) ----------------------------------------------------------------------------------------------------
export const customerTripListViewValues = ["CURRENT", "PLANNING", "HISTORY"];
export const customerTripsQuerySchema = z.strictObject({
    /** CURRENT: ACTIVE then UPCOMING. PLANNING: PLANNING. HISTORY: COMPLETED and CANCELLED. */
    view: z.enum(customerTripListViewValues).default("CURRENT"),
    cursor: cursor.optional(),
    limit: pageLimit,
});
export const customerTripSummarySchema = z.strictObject({
    id: uuid,
    title: z.string().min(1).max(200),
    status: z.enum(tripStatusValues),
    startDate: localDate.nullable(),
    startTimeZone: timeZone.nullable(),
    endDate: localDate.nullable(),
    endTimeZone: timeZone.nullable(),
    destinations: z
        .array(z.strictObject({
        name: z.string().min(1).max(120),
        countryCode: z.string().length(2).nullable(),
    }))
        .max(10),
    party: z.strictObject({
        adults: z.number().int().min(0),
        children: z.number().int().min(0),
        infants: z.number().int().min(0),
    }),
    primaryItineraryId: uuid.nullable(),
    version,
    updatedAt: instant,
});
export const customerTripsPageSchema = z.strictObject({
    items: z.array(customerTripSummarySchema).max(50),
    nextCursor: cursor.nullable(),
});
export const customerTripDetailSchema = z.strictObject({
    trip: customerTripSummarySchema,
    itinerary: z
        .strictObject({
        id: uuid,
        title: z.string().min(1).max(200),
        itemCount: z.number().int().min(0),
        days: z
            .array(z.strictObject({ localDate, timeZone, itemCount: z.number().int().min(0) }))
            .max(60),
    })
        .nullable(),
    /** Open Action Center items that name this trip (same shape and ids as the Action Center). */
    openActions: z.array(customerActionItemV2Schema).max(50),
    savedItemIds: z.array(uuid).max(100),
});
// --- Trip create, edit and cancel (CHT-736, plan item 6) ----------------------------------------------------------------
/** Why a customer cancels a trip (parity-tested with the domain `tripCancelReasonCodes`). Free text is never taken. */
export const tripCancelReasonCodeValues = [
    "CHANGE_OF_PLANS",
    "FOUND_ALTERNATIVE",
    "TRAVEL_NOT_POSSIBLE",
    "OTHER",
];
/**
 * The basics of a trip, named as the summary names them, so a form can start from a read. The schema only shapes and bounds the
 * input; the domain (`validateTripDraft`) judges it and answers with the field it refused. A destination carries a name and a
 * country only: what staff or the agent added to a place (locality, zone, dates) is kept by the server when the place stays.
 * A `party` of `null` means not stated.
 */
const tripBasics = {
    title: z.string().max(400),
    startDate: localDate.nullable(),
    startTimeZone: timeZone.nullable(),
    endDate: localDate.nullable(),
    endTimeZone: timeZone.nullable(),
    party: z
        .strictObject({
        adults: z.number().int().min(0).max(1000),
        children: z.number().int().min(0).max(1000),
        infants: z.number().int().min(0).max(1000),
    })
        .nullable(),
    destinations: z
        .array(z.strictObject({ name: z.string().max(400), countryCode: z.string().max(8) }))
        .max(40),
};
/** `POST /customer/trips`. The trip starts PLANNING at version 1. */
export const customerTripCreateBodySchema = z.strictObject(tripBasics);
/** `POST /customer/trips/:tripId/edit`. The whole set of basics replaces the old one; a stale version is `409`. */
export const customerTripEditBodySchema = z.strictObject({
    expectedVersion: version,
    ...tripBasics,
});
/** `POST /customer/trips/:tripId/cancel`. A stale version is `409`. */
export const customerTripCancelBodySchema = z.strictObject({
    expectedVersion: version,
    reasonCode: z.enum(tripCancelReasonCodeValues).optional(),
});
/** `POST /customer/trips/:tripId/confirm`. PLANNING to UPCOMING. An incomplete plan is `422`; a stale version is `409`. */
export const customerTripConfirmBodySchema = z.strictObject({ expectedVersion: version });
/** `POST /customer/trips/:tripId/reopen`. UPCOMING back to PLANNING. A stale version is `409`. */
export const customerTripReopenBodySchema = z.strictObject({ expectedVersion: version });
/** What a trip command answers: the trip and its new version. The client reads the trip again for the rest. */
export const customerTripCommandOutcomeSchema = z.strictObject({
    tripId: uuid,
    status: z.enum(tripStatusValues),
    version,
});
export const tripRefusalCodeValues = [
    /** The trip changed since the customer read it: `409`. Read it again. */
    "trip.version_conflict",
    /** The trip is under way: its basics are not editable: `409`. */
    "trip.not_editable",
    /** The trip is closed (completed or cancelled): `409`. */
    "trip.terminal",
    /** The command does not fit the state of the trip: `409`. */
    "trip.transition_invalid",
    /** An upcoming trip must keep both dates and a destination: `422`. */
    "trip.plan_incomplete",
    /** The input was refused by the domain rules: `422`, with the `field`. */
    "trip.invalid",
];
/** The fields a refusal can name: the body fields, with party and destinations as one field each. */
export const tripRefusalFieldValues = [
    "title",
    "startDate",
    "endDate",
    "startTimeZone",
    "endTimeZone",
    "party",
    "destinations",
    "expectedVersion",
    "reasonCode",
    "body",
];
/** The `tripRefusal` key of a trip command's `409` or `422` body, beside the shared envelope. Codes and a field name only. */
export const tripRefusalSchema = z.strictObject({
    code: z.enum(tripRefusalCodeValues),
    field: z.enum(tripRefusalFieldValues).nullable(),
});
// --- Saved (CHT-737) ----------------------------------------------------------------------------------------------------
export const customerSavedQuerySchema = z.strictObject({
    targetType: z.enum(savedItemTargetTypeValues).optional(),
    cursor: cursor.optional(),
    limit: pageLimit,
});
/** `POST /customer/saved`. Idempotent by the active-save uniqueness and by `Idempotency-Key`. */
export const customerSaveBodySchema = z.strictObject({
    targetType: z.enum(savedItemTargetTypeValues),
    targetId: uuid,
    /** Where the save came from, for analytics only (no content). */
    origin: z.enum(["CHAT_CARD", "CONVERSATIONAL", "CATALOG_DETAIL", "TRIP", "ITINERARY"]),
});
export const customerSavedItemSchema = z.strictObject({
    id: uuid,
    targetType: z.enum(savedItemTargetTypeValues),
    targetId: uuid,
    /** `AVAILABLE`: read the live entity. `UNAVAILABLE`: show `titleSnapshot` with the historical state. */
    availability: z.enum(["AVAILABLE", "UNAVAILABLE"]),
    titleSnapshot: z.string().min(1).max(200).nullable(),
    /** The trip of a saved plan (`ITINERARY`), so the app can open it; `null` for any other kind. */
    tripId: uuid.nullable(),
    savedAt: instant,
});
export const customerSavedPageSchema = z.strictObject({
    items: z.array(customerSavedItemSchema).max(50),
    nextCursor: cursor.nullable(),
});
/**
 * What the Saved detail reads of a published catalog item (`GET /customer/catalog/items/:itemId`, the live canonical row). The
 * server's own view schema parses dates as `Date`, so a JSON client cannot reuse it: this is the JSON form of the few fields the
 * screen shows. It is a loose object on purpose (the full view has many more keys and is owned by the catalog), and the screen
 * draws only the named fields.
 */
export const customerSavedCatalogItemSchema = z.looseObject({
    id: uuid,
    itemType: z.string().min(1).max(64),
    name: z.string().min(1),
    shortDescription: z.string().nullable(),
    description: z.string().nullable(),
    location: z.looseObject({
        locality: z.string().nullable(),
        region: z.string().nullable(),
        countryCode: z.string().nullable(),
    }),
});
// --- Profile and notification preferences (CHT-739) ---------------------------------------------------------------------
export const contactChannelPreferenceValues = ["IN_APP", "EMAIL", "PHONE"];
export const customerProfileViewSchema = z.strictObject({
    customerId: uuid,
    fullName: z.string().min(1).max(200).nullable(),
    preferredName: z.string().min(1).max(100).nullable(),
    email: z.string().min(3).max(320).nullable(),
    phoneE164: z.string().min(4).max(20).nullable(),
    locale: z.string().min(2).max(35).nullable(),
    contactChannelPreference: z.enum(contactChannelPreferenceValues).nullable(),
    /** The effective PRIMARY concierge, customer-safe. Never a backup, a queue or a coverage gap. 200 = `staff_users_display_name_length`. */
    concierge: z
        .strictObject({ displayName: z.string().min(1).max(200), since: localDate.nullable() })
        .nullable(),
    version,
});
/** `POST /customer/profile`. Only the three editable columns; a stale version is 409 `profile.version_conflict`. */
export const customerProfileUpdateBodySchema = z
    .strictObject({
    expectedVersion: version,
    preferredName: z.string().trim().min(1).max(100).nullable().optional(),
    locale: z.string().min(2).max(35).nullable().optional(),
    contactChannelPreference: z.enum(contactChannelPreferenceValues).nullable().optional(),
})
    .refine((value) => value.preferredName !== undefined ||
    value.locale !== undefined ||
    value.contactChannelPreference !== undefined, "at least one field is required");
/**
 * Optional notification topics. Service messages (a concierge reply, a payment due, a confirmation) are transactional and have
 * no toggle: they are not in this list.
 */
export const notificationTopicValues = ["TRIP_INSPIRATION", "PRODUCT_NEWS"];
export const customerNotificationPreferencesSchema = z.strictObject({
    topics: z.strictObject({ TRIP_INSPIRATION: z.boolean(), PRODUCT_NEWS: z.boolean() }),
    version,
});
export const customerNotificationPreferencesBodySchema = z.strictObject({
    expectedVersion: version,
    topics: z.strictObject({ TRIP_INSPIRATION: z.boolean(), PRODUCT_NEWS: z.boolean() }),
});
